import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Cookie, Settings } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { initializeGA } from '@/hooks/useAnalytics';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';

const cookieTexts = {
  it: {
    title: 'La tua privacy conta',
    description: 'Usiamo cookie per migliorare la tua esperienza.',
    descriptionFull: 'Usiamo cookie per migliorare la tua esperienza, personalizzare contenuti e analizzare il traffico. Cliccando "Accetta Tutti" acconsenti all\'uso dei cookie.',
    privacyLink: 'Privacy Policy',
    customize: 'Personalizza',
    essentialOnly: 'Solo Essenziali',
    acceptAll: 'Accetta Tutti',
    settingsTitle: 'Preferenze Cookie',
    settingsDescription: 'Gestisci le tue preferenze cookie. I cookie essenziali non possono essere disabilitati perché necessari al funzionamento del sito.',
    essentialLabel: 'Cookie Essenziali (Obbligatori)',
    essentialDesc: 'Necessari per il funzionamento del sito. Non possono essere disabilitati.',
    analyticsLabel: 'Cookie Analitici',
    analyticsDesc: 'Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo informazioni in modo anonimo.',
    marketingLabel: 'Cookie Marketing',
    marketingDesc: 'Tracciano la tua attività online per aiutare gli inserzionisti a mostrare pubblicità più pertinenti.',
    savePreferences: 'Salva Preferenze',
  },
  en: {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your experience.',
    descriptionFull: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    privacyLink: 'Privacy Policy',
    customize: 'Customize',
    essentialOnly: 'Essential Only',
    acceptAll: 'Accept All',
    settingsTitle: 'Cookie Preferences',
    settingsDescription: 'Manage your cookie preferences. Essential cookies cannot be disabled as they are required for the site to function.',
    essentialLabel: 'Essential Cookies (Required)',
    essentialDesc: 'These cookies are necessary for the website to function and cannot be disabled.',
    analyticsLabel: 'Analytics Cookies',
    analyticsDesc: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    marketingLabel: 'Marketing Cookies',
    marketingDesc: 'These cookies track your online activity to help advertisers deliver more relevant advertising.',
    savePreferences: 'Save Preferences',
  },
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(() => !localStorage.getItem(COOKIE_CONSENT_KEY));
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const isMobile = useIsMobile();
  
  let lang: 'it' | 'en' = 'it';
  try {
    const { lang: landingLang } = useLandingLanguage();
    lang = landingLang;
  } catch {
    lang = navigator.language.startsWith('it') ? 'it' : 'en';
  }
  const ct = cookieTexts[lang];

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    if (prefs.analytics) {
      initializeGA();
    }
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({ essential: true, analytics: true, marketing: true });
  };

  const acceptEssential = () => {
    savePreferences({ essential: true, analytics: false, marketing: false });
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Semi-transparent overlay to prevent accidental clicks */}
      <div 
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" 
        aria-hidden="true"
      />

      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-6 animate-fade-up"
        role="region"
        aria-label="Cookie consent banner"
      >
        <Card className="max-w-4xl mx-auto shadow-premium-xl border-2">
          <CardHeader className="pb-2 sm:pb-4 p-4 sm:p-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <Cookie className="h-5 w-5 sm:h-6 sm:w-6 text-primary mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{ct.title}</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {isMobile ? ct.description : ct.descriptionFull}{' '}
                  <a href="/privacy" className="underline hover:text-primary">
                    {ct.privacyLink}
                  </a>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {isMobile ? (
              /* Mobile: compact 2-button layout */
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  size="sm"
                  className="flex-1 h-10"
                >
                  <Settings className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  {ct.customize}
                </Button>
                <Button
                  onClick={acceptAll}
                  size="sm"
                  className="flex-1 h-10"
                >
                  {ct.acceptAll}
                </Button>
              </div>
            ) : (
              /* Desktop: 3-button layout */
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="flex-1"
                >
                  <Settings className="h-4 w-4 mr-2" aria-hidden="true" />
                  {ct.customize}
                </Button>
                <Button
                  onClick={acceptEssential}
                  variant="outline"
                  className="flex-1"
                >
                  {ct.essentialOnly}
                </Button>
                <Button
                  onClick={acceptAll}
                  className="flex-1"
                >
                  {ct.acceptAll}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{ct.settingsTitle}</SheetTitle>
            <SheetDescription>{ct.settingsDescription}</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox id="essential" checked={true} disabled />
                <div className="flex-1">
                  <label htmlFor="essential" className="text-sm font-medium">{ct.essentialLabel}</label>
                  <p className="text-sm text-muted-foreground mt-1">{ct.essentialDesc}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked as boolean })}
                />
                <div className="flex-1">
                  <label htmlFor="analytics" className="text-sm font-medium cursor-pointer">{ct.analyticsLabel}</label>
                  <p className="text-sm text-muted-foreground mt-1">{ct.analyticsDesc}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked as boolean })}
                />
                <div className="flex-1">
                  <label htmlFor="marketing" className="text-sm font-medium cursor-pointer">{ct.marketingLabel}</label>
                  <p className="text-sm text-muted-foreground mt-1">{ct.marketingDesc}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <Button onClick={acceptEssential} variant="outline" className="flex-1">
              {ct.essentialOnly}
            </Button>
            <Button onClick={() => savePreferences(preferences)} className="flex-1">
              {ct.savePreferences}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
